import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const SanLuisObispoCountyPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?county=San+Luis+Obispo&state=CA&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching SLO properties:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>San Luis Obispo County Real Estate | Buy or Sell a Home in SLO County | Truc Doan</title>
        <meta
          name="description"
          content="Looking to buy or sell a home in San Luis Obispo County, CA? Truc Doan is your trusted SLO County real estate agent. Explore homes for sale in San Luis Obispo, Paso Robles, Arroyo Grande, Pismo Beach, Atascadero, and more. Get a free home value estimate today."
        />
        <meta
          name="keywords"
          content="San Luis Obispo County real estate, homes for sale San Luis Obispo County CA, buy a home SLO County, sell a home San Luis Obispo, SLO County realtor, San Luis Obispo homes for sale, Paso Robles real estate, Arroyo Grande homes, Pismo Beach real estate, Atascadero homes for sale, Morro Bay real estate, Grover Beach homes, Nipomo real estate, Cambria homes, Templeton real estate, Truc Doan San Luis Obispo, SLO County home value, San Luis Obispo real estate agent"
        />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/san-luis-obispo-county`} />
        <meta property="og:title" content="San Luis Obispo County Real Estate | Buy or Sell a Home in SLO County | Truc Doan" />
        <meta property="og:description" content="Your trusted SLO County real estate expert. Browse homes for sale in San Luis Obispo, Paso Robles, Pismo Beach, Arroyo Grande & more. Free home value estimates." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/san-luis-obispo-county`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="San Luis Obispo County, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": SEO_CONFIG.AGENT_PHONE,
            "email": SEO_CONFIG.AGENT_EMAIL,
            "url": `${SEO_CONFIG.DOMAIN}/san-luis-obispo-county`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan is a top-rated real estate agent serving San Luis Obispo County, CA. Specializing in helping buyers find their dream home and sellers maximize their home value across all SLO County cities.",
            "areaServed": {
              "@type": "AdministrativeArea",
              "name": "San Luis Obispo County, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button
          onClick={() => navigate('/')}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            padding: '10px 20px',
            background: 'rgb(14, 165, 233)',
            color: 'rgb(243, 244, 246)',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            zIndex: 10
          }}
        >
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>{t('slo.h1')}</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            {t('slo.subtitle')}
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>{t('slo.whyTitle')}</h2>
          <p>{t('slo.whyP1')}</p>
          <p>{t('slo.whyP2')}</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>{t('county.buyingHome', { county: 'San Luis Obispo County' })}</h2>
          <p>{t('slo.buyingP')}</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>{t('slo.sloCity')}</h3>
              <p>{t('slo.sloCityDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('slo.pasoRobles')}</h3>
              <p>{t('slo.pasoRoblesDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('slo.pismo')}</h3>
              <p>{t('slo.pismoDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('slo.arroyoGrande')}</h3>
              <p>{t('slo.arroyoGrandeDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('slo.atascadero')}</h3>
              <p>{t('slo.atascaderoDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('slo.morroBay')}</h3>
              <p>{t('slo.morroBayDesc')}</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>{t('county.marketSnapshot', { county: 'San Luis Obispo County' })}</h2>
          <p>{t('slo.marketP')}</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>{t('county.marketStat')}</th>
                <th>{t('county.value')}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t('county.medianHomePrice')} (SLO County)</td>
                <td>~$850,000</td>
              </tr>
              <tr>
                <td>{t('county.avgDaysOnMarket')}</td>
                <td>30–45 Days</td>
              </tr>
              <tr>
                <td>{t('county.mostActivePriceRange')}</td>
                <td>$600K – $1.2M</td>
              </tr>
              <tr>
                <td>{t('county.topBuyerCities')}</td>
                <td>San Luis Obispo, Paso Robles, Arroyo Grande, Atascadero</td>
              </tr>
              <tr>
                <td>{t('county.topSellerMarkets')}</td>
                <td>Pismo Beach, San Luis Obispo, Cambria</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="idxtown-vibe">
          <h2>{t('county.sellingHome', { county: 'San Luis Obispo County' })}</h2>
          <p>{t('slo.sellingP1')}</p>
          <p dangerouslySetInnerHTML={{__html: t('slo.sellingP2')}} />
          <div className="amenities-list">
            <div className="amenity">
              <h3>{t('county.accuratePricing')}</h3>
              <p>{t('slo.pricingDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('county.professionalMarketing')}</h3>
              <p>{t('slo.marketingDesc')}</p>
            </div>
            <div className="amenity">
              <h3>{t('county.expertNegotiation')}</h3>
              <p>{t('slo.negotiationDesc')}</p>
            </div>
          </div>
        </section>

        <section className="properties-section">
          <h2>{t('county.currentHomesForSale', { county: 'San Luis Obispo County' })}</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div
                  key={property.listing_key}
                  className="property-card"
                  onClick={() => navigate(`/listing/${property.listing_key}`)}
                  style={{
                    backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">
                    {formatPropertyAddress(property)}, CA {property.postal_code}
                  </div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>
              {t('county.browseListings', { county: 'San Luis Obispo County' })}
            </p>
          )}
        </section>

        <section className="cta-section">
          <h2>{t('county.readyToBuyOrSell', { county: 'San Luis Obispo County' })}</h2>
          <p>{t('county.contactForConsultation')}</p>
          <div className="cta-buttons">
            <a href={`mailto:${SEO_CONFIG.AGENT_EMAIL}?subject=San Luis Obispo County Real Estate Inquiry`} className="cta-button primary">
              {t('county.emailAgent')}
            </a>
            <a href={`tel:${SEO_CONFIG.AGENT_PHONE}`} className="cta-button secondary">
              {t('county.callAgent', { phone: SEO_CONFIG.AGENT_PHONE })}
            </a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>
              {t('county.searchCountyListings', { county: 'SLO' })}
            </button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>
          Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information. Any amount of compensation or commission stated is made only to participants of CRMLS or an MLS that has entered into a datashare agreement with CRMLS.
        </span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default SanLuisObispoCountyPage;
